/**
 * The pages a reader had already loaded, kept across a trip into a case.
 *
 * useScrollMemory puts the list back where you left it, and says in its own
 * comment what it cannot do: coming back re-fetches only the FIRST page, so a
 * reader who had scrolled four pages deep lands near the top of a list that is
 * now a quarter as long, and the offset clamps to nothing. Remembering the
 * offset is only half the problem — the rows have to be there too.
 *
 * sessionStorage, matching useScrollMemory:
 *
 *   - it survives a reload and the browser's back button, which is the whole
 *     point here;
 *   - it is scoped to the tab, so two tabs on different clients cannot serve
 *     each other's rows, and opening a case in a new tab leaves the original
 *     tab's list untouched;
 *   - it is cleared when the tab closes, which matters because these rows go
 *     stale. A case moves through In-Process, AI Assessed and Review
 *     Completed, and the list polls every few seconds for exactly that reason.
 *     A list restored from yesterday would be confidently wrong.
 *
 * The cache is therefore a short-lived continuation of one reading session,
 * not a store. It is revalidated on restore, never trusted as the final word.
 */

const KEY_PREFIX = "list:";

/**
 * What can honestly survive a round trip through JSON.
 *
 * A row carries a rendered React element — the country flag — and JSON turns
 * it into a plain {key, props, _owner, _store}. React then refuses it with
 * "Objects are not valid as a React child" and the page renders nothing at
 * all. Anything element-shaped is dropped here and re-derived on restore,
 * because an element is a view, not data, and has no business in a cache.
 *
 * assessmentData goes too: it is the whole untransformed API row, kept "for
 * reference", and it is the bulk of the payload. Dropping it keeps the entry
 * small enough that a deep list is nowhere near the storage budget.
 */
const REACT_ELEMENT = typeof Symbol === "function" ? Symbol.for("react.element") : null;

const isRenderable = (value) =>
  value !== null &&
  typeof value === "object" &&
  (value.$$typeof === REACT_ELEMENT || value.$$typeof !== undefined);

export const serialisableRow = (row) => {
  if (!row || typeof row !== "object") return row;
  const out = {};
  Object.entries(row).forEach(([key, value]) => {
    if (key === "assessmentData") return;
    if (isRenderable(value)) return;
    out[key] = value;
  });
  return out;
};

/** Long enough to cover reading a case, short enough that states are fresh. */
export const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * A ceiling on what is worth keeping. Well under the 5MB storage budget even
 * at the widest row, and past this the restore costs more than the re-fetch.
 */
export const MAX_CACHED_ROWS = 500;

const storage = () => {
  try {
    return window.sessionStorage;
  } catch {
    // Private mode, or storage disabled. The cache is a convenience.
    return null;
  }
};

/**
 * @param key   what the rows belong to — include the client and the route, so
 *              one list never serves another's rows
 * @param now   injected for tests
 */
export const readListCache = (key, now = Date.now()) => {
  const store = storage();
  if (!store || !key) return null;

  try {
    const raw = store.getItem(`${KEY_PREFIX}${key}`);
    if (!raw) return null;

    const entry = JSON.parse(raw);
    if (!entry || !Array.isArray(entry.rows) || entry.rows.length === 0) {
      return null;
    }

    // Stale is worse than absent here: a wrong status reads as fact.
    if (!Number.isFinite(entry.savedAt) || now - entry.savedAt > CACHE_TTL_MS) {
      store.removeItem(`${KEY_PREFIX}${key}`);
      return null;
    }

    return {
      rows: entry.rows,
      page: Number.isFinite(entry.page) && entry.page > 0 ? entry.page : 1,
      hasMore: entry.hasMore !== false,
      savedAt: entry.savedAt,
    };
  } catch {
    // Malformed or unreadable: behave as though nothing was stored.
    return null;
  }
};

export const writeListCache = (key, { rows, page, hasMore } = {}, now = Date.now()) => {
  const store = storage();
  if (!store || !key) return;

  if (!Array.isArray(rows) || rows.length === 0) {
    clearListCache(key);
    return;
  }

  try {
    store.setItem(
      `${KEY_PREFIX}${key}`,
      JSON.stringify({
        rows: rows.slice(0, MAX_CACHED_ROWS).map(serialisableRow),
        page: Number.isFinite(page) && page > 0 ? page : 1,
        hasMore: hasMore !== false,
        savedAt: now,
      }),
    );
  } catch {
    // Over quota, most likely. Drop this list's entry rather than leaving a
    // half-written one, and let the next visit fetch normally.
    clearListCache(key);
  }
};

export const clearListCache = (key) => {
  const store = storage();
  if (!store || !key) return;
  try {
    store.removeItem(`${KEY_PREFIX}${key}`);
  } catch {
    /* storage disabled mid-session */
  }
};

/**
 * Drop every cached list.
 *
 * For the moments when what is on screen must not survive: signing out, and
 * switching client or line, where the next reader is a different person or the
 * rows belong to a different backend.
 */
export const clearAllListCaches = () => {
  const store = storage();
  if (!store) return;
  try {
    Object.keys(store)
      .filter((k) => k.startsWith(KEY_PREFIX))
      .forEach((k) => store.removeItem(k));
  } catch {
    /* storage disabled mid-session */
  }
};

/** A row's stable identity — id when there is one, else whichever case-number field it carries. */
const idOf = (row) => (row?.id ?? row?.fullCaseId ?? row?.caseId ?? null);

/**
 * Drops a row the list has already shown once.
 *
 * "Load more" pages by page_num against a newest-first, actively growing
 * list — a case created between two page fetches pushes every later row down
 * by one, so the next page can repeat a row the previous page already
 * returned. Appending blindly, which is what the isLoadingMore branch of
 * fetchCases otherwise does, counts and renders that case twice; enough of it
 * over a long scroll compounds into a shown count that exceeds the server's
 * own total for the same filters. Kept by first occurrence, which is the
 * newest-first position a case is meant to render at.
 *
 * Pure so it can be tested without the page.
 */
export const dedupeRows = (rows) => {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const id = idOf(row);
    const key = id === null || id === undefined ? null : String(id);
    // No identity to compare against — keep it rather than guess it's a dupe.
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Fold a freshly fetched first page back into a restored list.
 *
 * Revalidating a restored list cannot use either of the list's existing
 * modes: appending duplicates every row, and replacing throws away the pages
 * the reader had scrolled through — which is the thing being fixed. So the
 * fresh page updates the rows it covers, in place, and anything genuinely new
 * goes on the front where the newest cases belong. Rows beyond the first page
 * are left alone; they are refreshed when the reader scrolls to them again.
 *
 * Pure so it can be tested without the page.
 */
export const mergeRefreshedPage = (existing = [], fresh = []) => {
  if (!Array.isArray(existing) || existing.length === 0) return fresh || [];
  if (!Array.isArray(fresh) || fresh.length === 0) return existing;

  const byId = new Map();
  fresh.forEach((row) => {
    const id = idOf(row);
    if (id !== null && id !== undefined) byId.set(String(id), row);
  });

  const seen = new Set();
  const updated = existing.map((row) => {
    const id = idOf(row);
    const key = id === null || id === undefined ? null : String(id);
    if (key && byId.has(key)) {
      seen.add(key);
      return byId.get(key);
    }
    return row;
  });

  // Cases that appeared since the reader left, newest-first at the top.
  const added = fresh.filter((row) => {
    const id = idOf(row);
    const key = id === null || id === undefined ? null : String(id);
    return key ? !seen.has(key) : false;
  });

  return added.length > 0 ? [...added, ...updated] : updated;
};

export default readListCache;
